import { toAbsoluteUrl } from "../../_theme/helpers";
import { memo } from "react";
import HeroSection from "./Components/HeroSection";
import { envConfig } from "../../utils/config";
import { useDashboardTabEntry } from "../modules/Dashboard/core/tabRights";

interface Feature {
    id: number;
    text: string;
}

// Constants
const INFRASTRUCTURE_PROGRESS_DATA: Feature[] = [
    {
        id: 1,
        text: "Multiple regulatory requirements are demystified through intuitive, gamified tools, step-by-step guides, and automated draft templates."
    },
    {
        id: 2,
        text: "Complex prescripts and regulations are used to generate outputs to facilitate actionable pathways. This makes legal frameworks not just accessible but empowering and educational."
    },
];

const OFFERINGS_DATA: Feature[] = [
    {
        id: 1,
        text: "Instant access to detailed guides, frameworks, and checklists aligned with South African law and WCG standards.​"
    },
    {
        id: 2,
        text: "Practical strategies for seamless collaboration between public and private partners.​"
    },
    {
        id: 3,
        text: "Smart solutions (ease-of-doing business and ease-of-governance) for reducing paperwork, lowering risk, and ensuring every project benefits both investors and citizens.​​"
    },
];




// Reusable list renderer
const FeatureList: React.FC<{ items: Feature[] }> = memo(({ items }) => (
    <ul className="fs-5 fw-light">
        {items.map((item) => (
            <li key={item.id} className="section-content mb-3">
                {item.text}
            </li>
        ))}
    </ul>
));

const OutcomeSection: React.FC = memo(() => (
    <div className="row flex-column-reverse flex-lg-row g-10 section-padding bg-white align-items-center">
        <div className="col-md-8 px-10 px-sm-15 px-md-10 px-lg-20 px-xl-20  pe-lg-5 pe-xl-5">
            <div>
                <p className="penoptic-evaluation-section-title mb-2">The WCG Virtual Platform is tailored, offering:</p>
                <FeatureList items={OFFERINGS_DATA} />
                <p className="section-content mt-5">
                    Join our growing community that’s redefining infrastructure delivery. The journey starts here: let your curiosity lead the way and discover how digital governance can turn vision into impact.
                </p>
            </div>
        </div>
        <div className="col-md-4 px-10 px-sm-15 px-md-10 px-lg-10 px-xl-20  ps-lg-5 ps-xl-5">
            <img
                src={toAbsoluteUrl("media/images/WCGOffering.jpg")}
                alt="Investment-Ready Portfolio Outcome"
                className="w-100 h-100 rounded"
                loading="lazy"
            />
        </div>
    </div>
));


const VisionSection: React.FC = memo(() => (
    <div className="row section-padding bg-white align-items-center" id="vision-section">
        <div className="col-md-4 px-10 px-sm-15 px-md-10 px-lg-20 px-xl-20  pe-lg-5 pe-xl-5">
            <img
                src={toAbsoluteUrl("media/images/GovernanceEngine.png")}
                alt="Western Cape Infrastructure Vision"
                className="w-100 h-100 rounded"
                loading="lazy"
            />
        </div>
        <div className="col-md-8 px-10 px-sm-15 px-md-10 px-lg-10 px-xl-20  ps-lg-5 ps-xl-5">
            <div>
                <p className="penoptic-evaluation-section-title mb-2">The Governance Engine Powering Infrastructure Progress</p>
                <p className="section-content mb-5">
                    A digital gateway where transparency meets innovation, and trust forms the bedrock of progress.
                </p>
                <p className="fs-3 fw-semibold">Step inside a world where:</p>
                <FeatureList items={INFRASTRUCTURE_PROGRESS_DATA} />
                <p className="section-content mb-5">
                    Here governance is the secret ingredient that transforms good ideas into transformative, investment-ready infrastructure.
                </p>
            </div>
        </div>
    </div>
));

// Main Component
const GovernanceToolsandMethods: React.FC = () => {
    const dashboardEntry = useDashboardTabEntry('stagegates', 'Governance Tools & Methods');
    return (
        <div className="overflow-auto about-us bg-white">
            <HeroSection videoSrc={`${envConfig.APP_MEDIA_URL}videos/public-pages/governamce-resources.mp4`}
                fallbackImage={`${envConfig.APP_MEDIA_URL}images/public-pages/governamce-resources.webp`}
                title="Governance Tools & Methods"
                subtitle="The Pillars of Transparent Infrastructure - Building trust, one transparent step at a time."
                imageSrc={toAbsoluteUrl('media/images/governance-resources.png')}
                imageAlt="Governance Tools & Methods"
                imageClassName="w-150px"
                {...dashboardEntry}
                scrollParentId="vision-section" />
            <VisionSection />
            <OutcomeSection />
        </div>
    )
};

export default GovernanceToolsandMethods;